import { useMountEffect } from "@react-hookz/web";
import { createContext, ReactNode } from "react";
import api, { isSuccess } from "util/api";
import { Todo } from "shared/todos/Todo";
import { CompletedTodo } from "./CompletedTodo";
import {
  ApiCompletedTodoCreateResponse,
  apiCompletedTodoIndexPath,
} from "apiRoutes/apiCompletedTodoRoutes";
import useTodosReducer from "./useTodosReducer";
import usePersistSignedOutCompletedTodosOnLoggin from "./usePersistSignedOutCompletedTodosOnLoggin";

interface Props {
  children: ReactNode;
  todos: Todo[];
  completedTodos?: CompletedTodo[] | null;
}

export type TodosContextValue = ReturnType<typeof useTodosReducer>;

const defaultState: TodosContextValue = [
  {
    state: "loading",
    todos: [],
    completedTodos: {
      signedIn: new Set(),
      signedOut: new Set(),
    },
  },
  () => {},
];

export const Context = createContext<TodosContextValue>(defaultState);

export default function TodosContextProvider(props: Props) {
  const { children } = props;
  const value = useTodosReducer(props);
  const [state, dispatch] = value;

  useMountEffect(() => {
    const fn = async () => {
      if (state.state === "loaded") {
        return;
      }

      const todos = await api<ApiCompletedTodoCreateResponse>(
        apiCompletedTodoIndexPath,
        "GET"
      );

      if (isSuccess(todos)) {
        dispatch({
          type: "SET_MULTIPLE_SIGNED_IN_COMPLETED_TODO",
          payload: {
            completedTodos: todos.response,
          },
        });
      } else {
        dispatch({ type: "LOADING_COMPLETE" });
      }
    };

    fn();
  });

  usePersistSignedOutCompletedTodosOnLoggin(value);

  return <Context.Provider value={value}>{children}</Context.Provider>;
}
